import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

/*
  src/pages/components/AgentesRobos.jsx

  Painel central dos robôs da Lumix:
  - Aviso de venda: dispara quando uma venda/O.S. é criada.
  - Régua de relacionamento: cron diário às 08:00.
  - Editor de mensagem do aviso de venda usando AgenteMensagemTemplate.

  Rotas esperadas:
  GET    /api/agentes
  GET    /api/agentes/:key
  PUT    /api/agentes/:key
  POST   /api/agentes/:key/testar
  POST   /api/agentes/:key/executar

  GET    /api/agentes/mensagens
  GET    /api/agentes/:key/mensagem
  POST   /api/agentes/:key/mensagem
  PUT    /api/agentes/:key/mensagem
  DELETE /api/agentes/:key/mensagem
  POST   /api/agentes/:key/mensagem/restaurar-padrao
*/

const AGENTES_PADRAO = [
  {
    key: 'aviso_venda',
    nome: 'Aviso de venda',
    subtitulo: 'Avise administradores quando uma nova venda for criada.',
    tipo: 'evento',
    ativo: true,
    canalModo: 'auto_por_loja',
    canalWhatsappId: '',
    horario: '',
    timezone: 'America/Boa_Vista',
    numerosAdmin: [],
    gatilho: 'Ao criar venda/O.S.',
    badge: 'Tempo real',
    temMensagem: true
  },
  {
    key: 'regua_relacionamento',
    nome: 'Régua de relacionamento',
    subtitulo: 'Verifica clientes elegíveis e executa a régua às 08:00.',
    tipo: 'cron',
    ativo: true,
    canalModo: 'auto_por_loja',
    canalWhatsappId: '',
    horario: '08:00',
    timezone: 'America/Boa_Vista',
    numerosAdmin: [],
    gatilho: 'Todos os dias às 08:00',
    badge: 'Cron diário',
    temMensagem: false
  }
];

const TEMPLATE_PADRAO_AVISO_VENDA = `🛍️ *Nova venda registrada!*

🏪 Loja: {{loja}}
👤 Cliente: {{cliente}}
🧑‍💼 Vendedor: {{vendedor}}
👨‍💻 Criada por: {{criadoPor}}
🧾 O.S.: #{{numeroOS}}
💰 Valor: {{valor}}
💳 Pagamento: {{pagamento}}
🕒 Data: {{data}}

A venda já foi salva na Lumix.`;

const VARIAVEIS_AVISO_VENDA = [
  'loja',
  'empresa',
  'unidade',
  'cliente',
  'vendedor',
  'criadoPor',
  'numeroOS',
  'valor',
  'pagamento',
  'data'
];

const Icon = {
  Bot: () => (
    <svg viewBox="0 0 24 24">
      <path d="M12 8V4H8" />
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M2 14h2M20 14h2M9 13v2M15 13v2" />
    </svg>
  ),
  Zap: () => (
    <svg viewBox="0 0 24 24">
      <path d="M13 2 3 14h8l-1 8 10-12h-8l1-8z" />
    </svg>
  ),
  Clock: () => (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  Save: () => (
    <svg viewBox="0 0 24 24">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <path d="M17 21v-8H7v8M7 3v5h8" />
    </svg>
  ),
  Play: () => (
    <svg viewBox="0 0 24 24">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  Send: () => (
    <svg viewBox="0 0 24 24">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  ),
  Plus: () => (
    <svg viewBox="0 0 24 24">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Refresh: () => (
    <svg viewBox="0 0 24 24">
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 16h6v6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 8h-6V2" />
    </svg>
  ),
  Message: () => (
    <svg viewBox="0 0 24 24">
      <path d="M21 15a4 4 0 0 1-4 4H7l-4 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  ),
  Copy: () => (
    <svg viewBox="0 0 24 24">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  Rotate: () => (
    <svg viewBox="0 0 24 24">
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v6h6" />
    </svg>
  )
};

function normalizarId(v) {
  if (!v) return '';
  if (typeof v === 'object') return String(v._id || v.id || v.toString?.() || '');
  return String(v);
}

function normalizarTelefone(v = '') {
  let n = String(v).replace(/\D/g, '');

  if (!n) return '';
  if (n.startsWith('55') && n.length >= 12) return n;
  if (n.length === 10 || n.length === 11) return `55${n}`;

  return n;
}

function formatarTelefone(n = '') {
  const c = String(n).replace(/\D/g, '');
  const s = c.startsWith('55') ? c.slice(2) : c;

  if (s.length === 11) return s.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (s.length === 10) return s.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');

  return c;
}

function arr(data, keys = []) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;

  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
  }

  return [];
}

function statusCanal(c = {}) {
  const s = String(c.runtime?.status || c.status || c.state || '').toUpperCase();

  if (c.runtime?.connected || c.connected) return 'CONNECTED';
  if (['CONNECTED', 'INCHAT', 'CHATSAVAILABLE', 'ISLOGGED'].includes(s)) return 'CONNECTED';

  return s || 'DISCONNECTED';
}

function normalizarCanal(c = {}) {
  const id = normalizarId(c._id || c.id || c.canalWhatsappId);
  const empresaId = normalizarId(c.empresaId?._id || c.empresaId);
  const unidadeId = normalizarId(c.unidadeId?._id || c.unidadeId || empresaId);
  const st = statusCanal(c);

  return {
    id,
    empresaId,
    unidadeId,
    nome: c.nome || c.apelido || c.sessionName || `Canal ${id.slice(-6)}`,
    unidadeNome:
      c.codigoLoja ||
      c.loja ||
      c.unidadeId?.codigoLoja ||
      c.empresaId?.codigoLoja ||
      c.unidadeId?.nome ||
      c.empresaId?.nome ||
      'Unidade',
    connected: st === 'CONNECTED',
    status: st
  };
}

function erro(e, fallback = 'Erro ao processar solicitação.') {
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    fallback
  );
}

function mergeAgentes(apiAgentes = []) {
  const mapa = new Map(apiAgentes.map((a) => [a.key, a]));

  return AGENTES_PADRAO.map((p) => {
    const remoto = mapa.get(p.key) || {};

    return {
      ...p,
      ...remoto,
      numerosAdmin: remoto.numerosAdmin || remoto.numeroAdmin || p.numerosAdmin || []
    };
  });
}

function payloadAgente(a) {
  return {
    key: a.key,
    ativo: !!a.ativo,
    tipo: a.tipo,
    canalModo: a.canalModo || 'auto_por_loja',
    canalWhatsappId: a.canalWhatsappId || null,
    horario: a.horario || '',
    timezone: a.timezone || 'America/Boa_Vista',
    numerosAdmin: a.numerosAdmin || [],
    config: a.config || {}
  };
}

function criarTemplateLocalPadrao(key = 'aviso_venda') {
  if (key !== 'aviso_venda') return null;

  return {
    key: 'aviso_venda',
    canal: 'whatsapp',
    nome: 'Aviso automático de venda',
    mensagem: TEMPLATE_PADRAO_AVISO_VENDA,
    ativo: true,
    variaveisDisponiveis: VARIAVEIS_AVISO_VENDA
  };
}

function aplicarPreviewTemplate(template = '', variaveis = {}) {
  return String(template || '').replace(/{{\s*([\w.]+)\s*}}/g, (_, chave) => {
    const valor = variaveis[chave];

    if (valor === undefined || valor === null || valor === '') return '-';

    return String(valor);
  });
}

function exemploPreviewMensagem(mensagem = '') {
  return aplicarPreviewTemplate(mensagem, {
    loja: 'Vizum 02',
    empresa: 'Óticas Vizum',
    unidade: 'Filial Centro',
    cliente: 'Maria Oliveira',
    vendedor: 'Vitor Afonso',
    criadoPor: 'Vitor Afonso',
    numeroOS: '1045',
    valor: 'R$ 599,00',
    pagamento: 'Pix R$ 599,00',
    data: '16/05/26, 15:40'
  });
}

export default function AgentesRobos({ showToast }) {
  const [loading, setLoading] = useState(true);
  const [canais, setCanais] = useState([]);
  const [agentes, setAgentes] = useState(AGENTES_PADRAO);
  const [activeKey, setActiveKey] = useState('aviso_venda');
  const [activeTab, setActiveTab] = useState('config');

  const [adminInput, setAdminInput] = useState('');
  const [busy, setBusy] = useState('');
  const [avisos, setAvisos] = useState([]);

  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateMensagem, setTemplateMensagem] = useState(criarTemplateLocalPadrao('aviso_venda'));
  const [templateOriginal, setTemplateOriginal] = useState(null);

  const agente = useMemo(
    () => agentes.find((a) => a.key === activeKey) || agentes[0],
    [agentes, activeKey]
  );

  const canaisOnline = useMemo(() => canais.filter((c) => c.connected), [canais]);

  const previewMensagem = useMemo(
    () => exemploPreviewMensagem(templateMensagem?.mensagem || ''),
    [templateMensagem?.mensagem]
  );

  const templateAlterado = useMemo(() => {
    if (!templateMensagem || !templateOriginal) return false;

    return (
      String(templateMensagem.nome || '') !== String(templateOriginal.nome || '') ||
      String(templateMensagem.mensagem || '') !== String(templateOriginal.mensagem || '') ||
      Boolean(templateMensagem.ativo) !== Boolean(templateOriginal.ativo)
    );
  }, [templateMensagem, templateOriginal]);

  const aviso = useCallback(
    (tipo, msg) => {
      const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;

      setAvisos((p) => [{ id, tipo, msg }, ...p].slice(0, 4));

      setTimeout(() => {
        setAvisos((p) => p.filter((a) => a.id !== id));
      }, tipo === 'ok' ? 4200 : 6500);

      if (showToast) {
        showToast(tipo === 'ok' ? 'success' : 'error', msg);
      }
    },
    [showToast]
  );

  const patch = useCallback((key, up) => {
    setAgentes((p) =>
      p.map((a) => (a.key === key ? { ...a, ...(typeof up === 'function' ? up(a) : up) } : a))
    );
  }, []);

  const patchTemplate = useCallback((up) => {
    setTemplateMensagem((p) => ({
      ...(p || criarTemplateLocalPadrao('aviso_venda')),
      ...(typeof up === 'function' ? up(p || criarTemplateLocalPadrao('aviso_venda')) : up)
    }));
  }, []);

  const carregarTemplate = useCallback(
    async (key = activeKey) => {
      const agenteAtual = AGENTES_PADRAO.find((a) => a.key === key);

      if (!agenteAtual?.temMensagem) {
        setTemplateMensagem(null);
        setTemplateOriginal(null);
        return;
      }

      setTemplateLoading(true);

      try {
        const { data } = await api.get(`/api/agentes/${key}/mensagem`);
        const template = data?.template || criarTemplateLocalPadrao(key);

        setTemplateMensagem(template);
        setTemplateOriginal(JSON.parse(JSON.stringify(template)));
      } catch (e) {
        const fallback = criarTemplateLocalPadrao(key);

        setTemplateMensagem(fallback);
        setTemplateOriginal(JSON.parse(JSON.stringify(fallback)));

        aviso(
          'erro',
          erro(e, 'Não foi possível carregar o modelo de mensagem. Usando modelo local temporário.')
        );
      } finally {
        setTemplateLoading(false);
      }
    },
    [activeKey, aviso]
  );

  const carregar = useCallback(async () => {
    setLoading(true);

    try {
      const [rc, ra] = await Promise.all([
        api.get('/api/whatsapp/canais'),
        api.get('/api/agentes')
      ]);

      setCanais(arr(rc.data, ['canais', 'channels']).map(normalizarCanal).filter((c) => c.id));
      setAgentes(mergeAgentes(arr(ra.data, ['agentes', 'robos', 'robots'])));
    } catch (e) {
      aviso('erro', erro(e, 'Não foi possível carregar os agentes. Verifique as rotas do backend.'));
      setAgentes(AGENTES_PADRAO);
    } finally {
      setLoading(false);
    }
  }, [aviso]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    carregarTemplate(activeKey);
  }, [activeKey, carregarTemplate]);

  const salvar = async (key) => {
    const a = agentes.find((x) => x.key === key);

    if (!a) return;

    setBusy(`save:${key}`);

    try {
      const { data } = await api.put(`/api/agentes/${key}`, payloadAgente(a));

      if (data?.agente) patch(key, data.agente);

      aviso('ok', data?.message || 'Agente salvo.');
    } catch (e) {
      aviso('erro', erro(e, 'Erro ao salvar agente.'));
    } finally {
      setBusy('');
    }
  };

  const testar = async (key) => {
    const a = agentes.find((x) => x.key === key);

    if (!a) return;

    setBusy(`test:${key}`);

    try {
      const { data } = await api.post(`/api/agentes/${key}/testar`, payloadAgente(a));

      aviso(data?.success === false ? 'erro' : 'ok', data?.message || 'Teste solicitado.');
    } catch (e) {
      aviso('erro', erro(e, 'Erro ao testar agente.'));
    } finally {
      setBusy('');
    }
  };

  const executar = async (key) => {
    const a = agentes.find((x) => x.key === key);

    if (!a) return;

    setBusy(`run:${key}`);

    try {
      const { data } = await api.post(`/api/agentes/${key}/executar`, payloadAgente(a));

      aviso(data?.success === false ? 'erro' : 'ok', data?.message || 'Execução solicitada.');
    } catch (e) {
      aviso('erro', erro(e, 'Erro ao executar agente.'));
    } finally {
      setBusy('');
    }
  };

  const salvarTemplate = async () => {
    if (!templateMensagem) return;

    if (!String(templateMensagem.mensagem || '').trim()) {
      aviso('erro', 'A mensagem do modelo não pode ficar vazia.');
      return;
    }

    setBusy(`template:${activeKey}`);

    try {
      const body = {
        nome: templateMensagem.nome || 'Aviso automático de venda',
        canal: templateMensagem.canal || 'whatsapp',
        ativo: templateMensagem.ativo !== false,
        mensagem: templateMensagem.mensagem,
        variaveisDisponiveis: templateMensagem.variaveisDisponiveis || VARIAVEIS_AVISO_VENDA
      };

      const { data } = await api.put(`/api/agentes/${activeKey}/mensagem`, body);
      const template = data?.template || body;

      setTemplateMensagem(template);
      setTemplateOriginal(JSON.parse(JSON.stringify(template)));

      aviso('ok', data?.message || 'Modelo de mensagem salvo com sucesso.');
    } catch (e) {
      aviso('erro', erro(e, 'Erro ao salvar modelo de mensagem.'));
    } finally {
      setBusy('');
    }
  };

  const restaurarTemplatePadrao = async () => {
    setBusy(`restore-template:${activeKey}`);

    try {
      const { data } = await api.post(`/api/agentes/${activeKey}/mensagem/restaurar-padrao`);
      const template = data?.template || criarTemplateLocalPadrao(activeKey);

      setTemplateMensagem(template);
      setTemplateOriginal(JSON.parse(JSON.stringify(template)));

      aviso('ok', data?.message || 'Modelo padrão restaurado.');
    } catch (e) {
      aviso('erro', erro(e, 'Erro ao restaurar modelo padrão.'));
    } finally {
      setBusy('');
    }
  };

  const copiarPreview = async () => {
    try {
      await navigator.clipboard.writeText(previewMensagem);
      aviso('ok', 'Prévia copiada.');
    } catch {
      aviso('erro', 'Não foi possível copiar a prévia.');
    }
  };

  const inserirVariavel = (variavel) => {
    const marcador = `{{${variavel}}}`;

    patchTemplate((t) => ({
      mensagem: `${t.mensagem || ''}${t.mensagem?.endsWith(' ') || !t.mensagem ? '' : ' '}${marcador}`
    }));
  };

  const addNumero = () => {
    const n = normalizarTelefone(adminInput);

    if (!n || n.length < 12) {
      aviso('erro', 'Informe um WhatsApp válido com DDD.');
      return;
    }

    if ((agente.numerosAdmin || []).includes(n)) {
      aviso('erro', 'Esse número já está na lista.');
      return;
    }

    patch(agente.key, (a) => ({
      numerosAdmin: [...(a.numerosAdmin || []), n]
    }));

    setAdminInput('');
  };

  const removeNumero = (n) => {
    patch(agente.key, (a) => ({
      numerosAdmin: (a.numerosAdmin || []).filter((x) => x !== n)
    }));
  };

  const selecionarAgente = (key) => {
    setActiveKey(key);

    const selecionado = AGENTES_PADRAO.find((a) => a.key === key);

    if (!selecionado?.temMensagem) {
      setActiveTab('config');
    }
  };

  if (loading) {
    return (
      <div className="agentes-page loading">
        <div className="loader" />
        <strong>Carregando agentes...</strong>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="agentes-page">
      <header className="agentes-header">
        <div>
          <h1>Agentes e robôs</h1>
          <p>Configure, teste e acompanhe as automações da ótica.</p>
        </div>

        <button className="soft-btn" onClick={carregar}>
          <Icon.Refresh />
          Atualizar
        </button>
      </header>

      {avisos.length > 0 && (
        <div className="avisos">
          {avisos.map((a) => (
            <div key={a.id} className={`aviso ${a.tipo}`}>
              {a.tipo === 'ok' ? <Icon.Zap /> : <Icon.Bot />}
              <span>{a.msg}</span>
            </div>
          ))}
        </div>
      )}

      <section className="metrics-strip">
        <div className="metric-item">
          <span>Robôs ativos</span>
          <strong>{agentes.filter((a) => a.ativo).length}</strong>
        </div>
        <div className="metric-item">
          <span>Canais online</span>
          <strong>
            {canaisOnline.length}/{canais.length}
          </strong>
        </div>
        <div className="metric-item">
          <span>Régua diária</span>
          <strong>08:00</strong>
        </div>
        <div className="metric-item">
          <span>Modelos</span>
          <strong>{templateMensagem ? 'Ativo' : 'Padrão'}</strong>
        </div>
      </section>

      <main className="agentes-grid">
        <aside className="agents-list">
          {agentes.map((a) => (
            <button
              type="button"
              key={a.key}
              onClick={() => selecionarAgente(a.key)}
              className={`agent-tab ${activeKey === a.key ? 'active' : ''}`}
            >
              <div className="agent-icon">
                {a.tipo === 'cron' ? <Icon.Clock /> : <Icon.Zap />}
              </div>

              <div>
                <strong>{a.nome}</strong>
                <span>{a.subtitulo}</span>
              </div>

              <em className={a.ativo ? 'on' : 'off'}>{a.ativo ? 'Ativo' : 'Pausado'}</em>
            </button>
          ))}
        </aside>

        <section className="agent-panel">
          <div className="panel-head">
            <div>
                            <h2>{agente.nome}</h2>
              <p>{agente.gatilho}</p>
            </div>

            <label className="switch">
              <input
                type="checkbox"
                checked={!!agente.ativo}
                onChange={(e) => patch(agente.key, { ativo: e.target.checked })}
              />
              <span />
            </label>
          </div>

          <div className="tabs-line">
            <button
              type="button"
              className={activeTab === 'config' ? 'active' : ''}
              onClick={() => setActiveTab('config')}
            >
              <Icon.Bot />
              Configuração
            </button>

            {agente.temMensagem && (
              <button
                type="button"
                className={activeTab === 'mensagem' ? 'active' : ''}
                onClick={() => setActiveTab('mensagem')}
              >
                <Icon.Message />
                Modelo de mensagem
                {templateAlterado && <b />}
              </button>
            )}
          </div>

          {activeTab === 'config' && (
            <>
              <div className="config-grid">
                <label className="field">
                  <span>Tipo</span>
                  <input
                    readOnly
                    value={agente.tipo === 'cron' ? 'Cron / agendado' : 'Evento / tempo real'}
                  />
                </label>

                <label className="field">
                  <span>Fuso</span>
                  <select
                    value={agente.timezone || 'America/Boa_Vista'}
                    onChange={(e) => patch(agente.key, { timezone: e.target.value })}
                  >
                    <option value="America/Boa_Vista">America/Boa_Vista</option>
                    <option value="America/Sao_Paulo">America/Sao_Paulo</option>
                  </select>
                </label>

                {agente.tipo === 'cron' && (
                  <label className="field">
                    <span>Horário</span>
                    <input
                      type="time"
                      value={agente.horario || '08:00'}
                      onChange={(e) => patch(agente.key, { horario: e.target.value })}
                    />
                  </label>
                )}

                <label className="field span-2">
                  <span>Canal WhatsApp</span>
                  <select
                    value={agente.canalModo || 'auto_por_loja'}
                    onChange={(e) => patch(agente.key, { canalModo: e.target.value })}
                  >
                    <option value="auto_por_loja">Automático pela loja da O.S.</option>
                    <option value="canal_fixo">Usar canal fixo</option>
                  </select>
                </label>

                {agente.canalModo === 'canal_fixo' && (
                  <label className="field span-2">
                    <span>Canal fixo</span>
                    <select
                      value={agente.canalWhatsappId || ''}
                      onChange={(e) => patch(agente.key, { canalWhatsappId: e.target.value })}
                    >
                      <option value="">Selecione</option>

                      {canais.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.connected ? '🟢' : '⚪'} {c.unidadeNome} · {c.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              <div className="box">
                <h3>Administradores notificados</h3>
                <p>Números que recebem avisos ou relatórios desse agente.</p>

                <div className="admin-input">
                  <input
                    value={adminInput}
                    onChange={(e) => setAdminInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addNumero()}
                    placeholder="Ex: 92981176885"
                  />

                  <button className="soft-btn dark" onClick={addNumero}>
                    <Icon.Plus />
                    Adicionar
                  </button>
                </div>

                <div className="chips">
                  {(agente.numerosAdmin || []).length === 0 && (
                    <div className="empty">Nenhum administrador configurado.</div>
                  )}

                  {(agente.numerosAdmin || []).map((n) => (
                    <span className="chip" key={n}>
                      {formatarTelefone(n)}
                      <button onClick={() => removeNumero(n)}>
                        <Icon.X />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="box flow">
                <h3>Fluxo do robô</h3>

                {agente.key === 'aviso_venda' ? (
                  <ol>
                    <li>Venda/O.S. é criada.</li>
                    <li>Backend identifica matriz, filial, vendedor, criador e valor.</li>
                    <li>O sistema busca o modelo de mensagem salvo no banco.</li>
                    <li>Canal é escolhido pela loja da O.S. ou pelo canal fixo.</li>
                    <li>Todos os administradores configurados recebem o aviso.</li>
                  </ol>
                ) : (
                  <ol>
                    <li>Cron roda todos os dias às 08:00.</li>
                    <li>Busca O.S. entregues da matriz e filiais.</li>
                    <li>Calcula marcos da régua.</li>
                    <li>Envia pelo canal correto da loja.</li>
                  </ol>
                )}
              </div>

              <footer className="actions">
                <button
                  className="soft-btn"
                  onClick={() => testar(agente.key)}
                  disabled={busy === `test:${agente.key}`}
                >
                  <Icon.Send />
                  {busy === `test:${agente.key}` ? 'Testando...' : 'Testar'}
                </button>

                <button
                  className="soft-btn"
                  onClick={() => executar(agente.key)}
                  disabled={busy === `run:${agente.key}`}
                >
                  <Icon.Play />
                  {busy === `run:${agente.key}` ? 'Executando...' : 'Executar agora'}
                </button>

                <button
                  className="primary-btn"
                  onClick={() => salvar(agente.key)}
                  disabled={busy === `save:${agente.key}`}
                >
                  <Icon.Save />
                  {busy === `save:${agente.key}` ? 'Salvando...' : 'Salvar agente'}
                </button>
              </footer>
            </>
          )}

          {activeTab === 'mensagem' && agente.temMensagem && (
            <div className="message-editor">
              {templateLoading ? (
                <div className="template-loading">
                  <div className="loader small" />
                  <strong>Carregando modelo de mensagem...</strong>
                </div>
              ) : (
                <>
                  <div className="editor-top">
                    <div>
                      <h3>Modelo de mensagem</h3>
                      <p>
                        Esse texto será usado automaticamente quando uma nova venda/O.S. for criada.
                      </p>
                    </div>

                    <div className="editor-status">
                      <span className={templateMensagem?.ativo !== false ? 'ativo' : 'pausado'}>
                        {templateMensagem?.ativo !== false ? 'Modelo ativo' : 'Modelo pausado'}
                      </span>
                    </div>
                  </div>

                  <div className="config-grid editor-fields">
                    <label className="field">
                      <span>Nome do modelo</span>
                      <input
                        value={templateMensagem?.nome || ''}
                        onChange={(e) => patchTemplate({ nome: e.target.value })}
                        placeholder="Aviso automático de venda"
                      />
                    </label>

                    <label className="field">
                      <span>Canal</span>
                      <input readOnly value={templateMensagem?.canal || 'whatsapp'} />
                    </label>

                    <label className="check-row span-2">
                      <input
                        type="checkbox"
                        checked={templateMensagem?.ativo !== false}
                        onChange={(e) => patchTemplate({ ativo: e.target.checked })}
                      />
                      <span>Usar este modelo nas próximas vendas criadas</span>
                    </label>
                  </div>

                  <div className="template-grid">
                    <div className="template-left">
                      <label className="field">
                        <span>Mensagem</span>
                        <textarea
                          value={templateMensagem?.mensagem || ''}
                          onChange={(e) => patchTemplate({ mensagem: e.target.value })}
                          rows={14}
                          placeholder="Digite o modelo da mensagem..."
                        />
                      </label>

                      <div className="vars-box">
                        <strong>Variáveis disponíveis</strong>
                        <p>Clique para inserir no final da mensagem.</p>

                        <div className="vars-list">
                          {(templateMensagem?.variaveisDisponiveis || VARIAVEIS_AVISO_VENDA).map(
                            (v) => (
                              <button key={v} type="button" onClick={() => inserirVariavel(v)}>
                                {`{{${v}}}`}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="template-right">
                      <div className="preview-card">
                        <div className="preview-head">
                          <strong>Prévia do aviso</strong>
                          <button type="button" onClick={copiarPreview}>
                            <Icon.Copy />
                            Copiar
                          </button>
                        </div>

                        <pre>{previewMensagem || 'A prévia aparecerá aqui.'}</pre>
                      </div>

                      <div className="hint-card">
                        <strong>Importante</strong>
                        <p>
                          Use as variáveis entre chaves para o sistema trocar automaticamente pelos
                          dados reais da venda.
                        </p>
                      </div>
                    </div>
                  </div>

                  <footer className="actions editor-actions">
                    <button
                      className="soft-btn"
                      onClick={() => carregarTemplate(activeKey)}
                      disabled={templateLoading || busy === `template:${activeKey}`}
                    >
                      <Icon.Refresh />
                      Recarregar
                    </button>

                    <button
                      className="soft-btn"
                      onClick={restaurarTemplatePadrao}
                      disabled={busy === `restore-template:${activeKey}`}
                    >
                      <Icon.Rotate />
                      {busy === `restore-template:${activeKey}` ? 'Restaurando...' : 'Restaurar padrão'}
                    </button>

                    <button
                      className="primary-btn"
                      onClick={salvarTemplate}
                      disabled={busy === `template:${activeKey}` || !templateAlterado}
                    >
                      <Icon.Save />
                      {busy === `template:${activeKey}` ? 'Salvando...' : 'Salvar modelo'}
                    </button>
                  </footer>
                </>
              )}
            </div>
          )}
        </section>
      </main>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .agentes-page{
    width:100%;
    min-height:100%;
    background:#fff;
    color:#0f172a;
    padding:18px;
    font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
  }

  .agentes-page svg{
    width:16px;
    height:16px;
    stroke:currentColor;
    stroke-width:2;
    fill:none;
    stroke-linecap:round;
    stroke-linejoin:round;
  }

  .loading{
    min-height:70vh;
    display:grid;
    place-items:center;
    align-content:center;
    gap:10px;
  }

  .loader{
    width:30px;
    height:30px;
    border:3px solid #e2e8f0;
    border-top-color:#1967d2;
    border-radius:999px;
    animation:spin .8s linear infinite;
  }

  .loader.small{
    width:22px;
    height:22px;
    border-width:2px;
  }

  @keyframes spin{
    to{transform:rotate(360deg)}
  }

  .agentes-header{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:14px;
    padding-bottom:14px;
    margin-bottom:12px;
    border-bottom:1px solid #e5e7eb;
  }

  .kicker,.pill{
    display:inline-flex;
    border:1px solid #dbeafe;
    background:#e8f0fe;
    color:#1967d2;
    border-radius:999px;
    padding:4px 8px;
    font-size:10px;
    font-weight:900;
    letter-spacing:.06em;
    text-transform:uppercase;
  }

  .agentes-header h1{
    margin:8px 0 4px;
    font-size:clamp(24px,3vw,32px);
    line-height:1;
    font-weight:850;
    letter-spacing:-.045em;
  }

  .agentes-header p{
    margin:0;
    max-width:760px;
    color:#64748b;
    font-size:13px;
    line-height:1.45;
  }

  .soft-btn,.primary-btn{
    min-height:36px;
    border:1px solid #e5e7eb;
    background:#fff;
    color:#334155;
    border-radius:999px;
    padding:0 13px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    gap:7px;
    cursor:pointer;
    font-size:12px;
    font-weight:850;
    white-space:nowrap;
  }

  .soft-btn.dark,.primary-btn{
    border-color:#0f172a;
    background:#0f172a;
    color:#fff;
  }

  .soft-btn:hover{
    background:#f8fafc;
  }

  .primary-btn:hover,.soft-btn.dark:hover{
    background:#1e293b;
  }

  button:disabled{
    opacity:.55;
    cursor:not-allowed;
  }

  .avisos{
    display:grid;
    gap:8px;
    margin-bottom:12px;
  }

  .aviso{
    border-radius:14px;
    padding:10px 12px;
    display:flex;
    gap:8px;
    align-items:center;
    font-size:12.5px;
    font-weight:750;
  }

  .aviso.ok{
    border:1px solid #bbf7d0;
    background:#f0fdf4;
    color:#166534;
  }

  .aviso.erro{
    border:1px solid #fecaca;
    background:#fef2f2;
    color:#991b1b;
  }

  .metrics-row{
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:10px;
    margin-bottom:12px;
  }

  .metric{
    border:1px solid #e5e7eb;
    border-radius:16px;
    background:#fff;
    padding:13px;
  }

  .metric span{
    display:block;
    color:#64748b;
    font-size:11px;
    font-weight:850;
    text-transform:uppercase;
    letter-spacing:.04em;
  }

  .metric strong{
    display:block;
    margin-top:7px;
    font-size:20px;
    line-height:1;
    font-weight:900;
    letter-spacing:-.04em;
  }

  .agentes-grid{
    display:grid;
    grid-template-columns:360px minmax(0,1fr);
    gap:14px;
    align-items:start;
  }

  .agents-list{
    display:grid;
    gap:8px;
    position:sticky;
    top:12px;
  }

  .agent-tab{
    width:100%;
    border:1px solid #e5e7eb;
    background:#fff;
    border-radius:18px;
    padding:12px;
    display:grid;
    grid-template-columns:38px minmax(0,1fr) auto;
    gap:10px;
    align-items:center;
    text-align:left;
    cursor:pointer;
  }

  .agent-tab.active{
    border-color:#bfdbfe;
    background:#f8fbff;
  }

  .agent-icon{
    width:38px;
    height:38px;
    border-radius:14px;
    background:#f8fafc;
    display:grid;
    place-items:center;
    color:#1967d2;
  }

  .agent-tab strong,.agent-tab span{
    display:block;
  }

  .agent-tab strong{
    font-size:13px;
    font-weight:900;
  }

  .agent-tab span{
    margin-top:3px;
    color:#64748b;
    font-size:11.5px;
    line-height:1.35;
  }

  .agent-tab em{
    border-radius:999px;
    padding:4px 8px;
    font-size:10.5px;
    font-style:normal;
    font-weight:900;
  }

  .agent-tab em.on{
    background:#dcfce7;
    color:#166534;
  }

  .agent-tab em.off{
    background:#fee2e2;
    color:#991b1b;
  }

  .agent-panel{
    border:1px solid #e5e7eb;
    border-radius:22px;
    background:#fff;
    padding:16px;
  }

  .panel-head{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:14px;
    padding-bottom:14px;
    border-bottom:1px solid #e5e7eb;
  }

  .panel-head h2{
    margin:8px 0 4px;
    font-size:22px;
    line-height:1;
    font-weight:900;
    letter-spacing:-.045em;
  }

  .panel-head p{
    margin:0;
    color:#64748b;
    font-size:13px;
    line-height:1.45;
  }

  .switch{
    position:relative;
    width:46px;
    height:26px;
    flex:0 0 auto;
  }

  .switch input{
    opacity:0;
    width:0;
    height:0;
  }

  .switch span{
    position:absolute;
    inset:0;
    background:#cbd5e1;
    border-radius:999px;
    cursor:pointer;
  }

  .switch span:before{
    content:'';
    width:20px;
    height:20px;
    background:#fff;
    border-radius:999px;
    position:absolute;
    left:3px;
    top:3px;
    transition:.16s;
  }

  .switch input:checked+span{
    background:#16a34a;
  }

  .switch input:checked+span:before{
    transform:translateX(20px);
  }

  .tabs-line{
    margin-top:14px;
    display:flex;
    gap:8px;
    flex-wrap:wrap;
  }

  .tabs-line button{
    border:1px solid #e5e7eb;
    background:#fff;
    min-height:36px;
    border-radius:999px;
    padding:0 13px;
    display:inline-flex;
    align-items:center;
    gap:7px;
    color:#475569;
    font-size:12px;
    font-weight:850;
    cursor:pointer;
    position:relative;
  }

  .tabs-line button.active{
    border-color:#bfdbfe;
    background:#e8f0fe;
    color:#1967d2;
  }

  .tabs-line button b{
    position:absolute;
    right:5px;
    top:4px;
    width:7px;
    height:7px;
    background:#f59e0b;
    border-radius:999px;
  }

  .config-grid{
    margin-top:14px;
    display:grid;
    grid-template-columns:repeat(2,minmax(0,1fr));
    gap:10px;
  }

  .field{
    display:grid;
    gap:6px;
  }

  .field.span-2,.span-2{
    grid-column:span 2;
  }

  .field span{
    color:#334155;
    font-size:11px;
    font-weight:850;
  }

  input,select,textarea{
    width:100%;
    border:1px solid #e5e7eb;
    border-radius:12px;
    background:#fff;
    color:#0f172a;
    outline:none;
    padding:0 11px;
    font-size:12.5px;
    font-weight:600;
    font-family:inherit;
  }

  input,select{
    min-height:39px;
  }

  textarea{
    min-height:280px;
    resize:vertical;
    padding:12px;
    line-height:1.55;
  }

  input:read-only{
    background:#f8fafc;
    color:#64748b;
  }

  input:focus,select:focus,textarea:focus{
    border-color:#bfdbfe;
    box-shadow:0 0 0 3px rgba(25,103,210,.10);
  }

  .box{
    margin-top:14px;
    border:1px solid #e5e7eb;
    border-radius:18px;
    padding:14px;
    background:#fff;
  }

  .box h3{
    margin:0;
    font-size:15px;
    font-weight:900;
    letter-spacing:-.03em;
  }

  .box p{
    margin:4px 0 10px;
    color:#64748b;
    font-size:12px;
  }

  .admin-input{
    display:grid;
    grid-template-columns:minmax(0,1fr) auto;
    gap:8px;
  }

  .chips{
    margin-top:10px;
    display:flex;
    flex-wrap:wrap;
    gap:7px;
  }

  .chip{
    min-height:31px;
    border:1px solid #e5e7eb;
    background:#fff;
    border-radius:999px;
    padding:0 5px 0 10px;
    display:inline-flex;
    align-items:center;
    gap:7px;
    font-size:12px;
    font-weight:800;
  }

  .chip button{
    width:22px;
    height:22px;
    border:0;
    border-radius:999px;
    background:#f1f5f9;
    color:#64748b;
    display:grid;
    place-items:center;
    cursor:pointer;
  }

  .empty{
    width:100%;
    border:1px dashed #e5e7eb;
    background:#f8fafc;
    border-radius:12px;
    padding:12px;
    color:#64748b;
    font-size:12px;
  }

  .flow ol{
    margin:8px 0 0;
    padding-left:19px;
    color:#334155;
    font-size:12.5px;
    line-height:1.7;
  }

  .actions{
    margin-top:14px;
    display:flex;
    justify-content:flex-end;
    gap:8px;
    flex-wrap:wrap;
  }

  .message-editor{
    margin-top:14px;
  }

  .template-loading{
    min-height:300px;
    display:grid;
    place-items:center;
    align-content:center;
    gap:10px;
    border:1px dashed #e5e7eb;
    border-radius:18px;
    color:#64748b;
  }

  .editor-top{
    display:flex;
    align-items:flex-start;
    justify-content:space-between;
    gap:14px;
    border:1px solid #e5e7eb;
    border-radius:18px;
    padding:14px;
    background:#f8fafc;
  }

  .editor-top h3{
    margin:0;
    font-size:16px;
    font-weight:950;
    letter-spacing:-.03em;
  }

  .editor-top p{
    margin:5px 0 0;
    color:#64748b;
    font-size:12.5px;
    line-height:1.45;
  }

  .editor-status span{
    display:inline-flex;
    border-radius:999px;
    padding:5px 9px;
    font-size:11px;
    font-weight:900;
    white-space:nowrap;
  }

  .editor-status .ativo{
    background:#dcfce7;
    color:#166534;
  }

  .editor-status .pausado{
    background:#fee2e2;
    color:#991b1b;
  }

  .check-row{
    border:1px solid #e5e7eb;
    border-radius:12px;
    min-height:39px;
    padding:0 11px;
    display:flex;
    align-items:center;
    gap:8px;
    color:#334155;
    font-size:12.5px;
    font-weight:800;
  }

  .check-row input{
    width:16px;
    min-height:16px;
    height:16px;
    padding:0;
  }

  .template-grid{
    margin-top:14px;
    display:grid;
    grid-template-columns:minmax(0,1.15fr) minmax(330px,.85fr);
    gap:12px;
    align-items:start;
  }

  .template-left,.template-right{
    display:grid;
    gap:12px;
  }

  .vars-box,.preview-card,.hint-card{
    border:1px solid #e5e7eb;
    border-radius:18px;
    background:#fff;
    padding:14px;
  }

  .vars-box strong,.preview-card strong,.hint-card strong{
    display:block;
    font-size:13px;
    font-weight:950;
  }

  .vars-box p,.hint-card p{
    margin:5px 0 0;
    color:#64748b;
    font-size:12px;
    line-height:1.5;
  }

  .vars-list{
    margin-top:10px;
    display:flex;
    flex-wrap:wrap;
    gap:7px;
  }

  .vars-list button{
    border:1px solid #dbeafe;
    background:#eff6ff;
    color:#1967d2;
    border-radius:999px;
    min-height:29px;
    padding:0 9px;
    font-size:11.5px;
    font-weight:900;
    cursor:pointer;
  }

  .preview-head{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    margin-bottom:10px;
  }

  .preview-head button{
    border:1px solid #e5e7eb;
    background:#fff;
    color:#334155;
    border-radius:999px;
    min-height:30px;
    padding:0 10px;
    display:inline-flex;
    align-items:center;
    gap:6px;
    font-size:11px;
    font-weight:900;
    cursor:pointer;
  }

  .preview-card pre{
    margin:0;
    white-space:pre-wrap;
    word-break:break-word;
    background:#f8fafc;
    border:1px solid #e5e7eb;
    border-radius:14px;
    padding:13px;
    color:#0f172a;
    font-family:inherit;
    font-size:12.5px;
    line-height:1.55;
  }

  .editor-actions{
    border-top:1px solid #e5e7eb;
    padding-top:14px;
  }

  @media(max-width:1050px){
    .agentes-grid{
      grid-template-columns:1fr;
    }

    .agents-list{
      position:static;
      grid-template-columns:repeat(2,minmax(0,1fr));
    }

    .template-grid{
      grid-template-columns:1fr;
    }
  }

  @media(max-width:760px){
    .agentes-page{
      padding:10px;
    }

    .agentes-header,.panel-head,.editor-top{
      display:grid;
    }

    .metrics-row,.agents-list,.config-grid{
      grid-template-columns:1fr;
    }

    .field.span-2,.span-2{
      grid-column:span 1;
    }

    .admin-input{
      grid-template-columns:1fr;
    }

    .soft-btn,.primary-btn{
      width:100%;
    }

    .actions{
      display:grid;
    }

    .agent-tab{
      grid-template-columns:36px minmax(0,1fr);
    }

    .agent-tab em{
      grid-column:1/-1;
      width:fit-content;
      margin-left:46px;
    }

    .template-grid{
      grid-template-columns:1fr;
    }

    .tabs-line button{
      flex:1;
      justify-content:center;
    }
  }

  /* =========================================================================
     V2 — PADRÃO GLOBAL DA PLATAFORMA
  ========================================================================= */
  .agentes-page{width:100%;min-height:100%;padding:18px 24px 34px!important;color:#171717!important;background:#fff!important;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Inter,Arial,sans-serif!important;-webkit-font-smoothing:antialiased;text-rendering:geometricPrecision}
  .agentes-page svg{width:15px!important;height:15px!important}
  .loading{min-height:60vh!important;background:#fff!important}
  .loader{width:24px!important;height:24px!important;border:2px solid #ddd!important;border-top-color:#111!important}
  .loader.small{width:20px!important;height:20px!important}
  .agentes-header{min-height:48px;align-items:center!important;padding:0 0 14px!important;margin:0 0 16px!important;border-bottom:1px solid #ececec!important}
  .kicker,.pill{display:none!important}
  .agentes-header h1{margin:0 0 3px!important;color:#171717!important;font-size:18px!important;line-height:1.2!important;font-weight:700!important;letter-spacing:-.025em!important}
  .agentes-header p{margin:0!important;max-width:none!important;color:#747474!important;font-size:11.5px!important;line-height:1.4!important}
  .soft-btn,.primary-btn{min-height:34px!important;padding:0 11px!important;border-radius:7px!important;font-size:11px!important;font-weight:650!important;box-shadow:none!important;transition:none!important}
  .soft-btn{border:1px solid #d8d8d8!important;color:#333!important;background:#fff!important}
  .soft-btn.dark,.primary-btn{border:1px solid #111!important;color:#fff!important;background:#111!important}
  .soft-btn:hover,.primary-btn:hover,.soft-btn.dark:hover{transform:none!important}
  .avisos{gap:6px!important;margin-bottom:12px!important}
  .aviso{padding:9px 10px!important;border-radius:8px!important;font-size:11px!important;font-weight:650!important;box-shadow:none!important}
  .metrics-strip{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:0;margin-bottom:14px;border-top:1px solid #ececec;border-bottom:1px solid #ececec;background:#fff}
  .metric-item{min-height:62px;padding:11px 14px;border-right:1px solid #ececec;background:#fff}
  .metric-item:last-child{border-right:0}
  .metric-item span{display:block;color:#777;font-size:9.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
  .metric-item strong{display:block;margin-top:5px;color:#171717;font-size:20px;line-height:1;font-weight:700;letter-spacing:-.03em}
  .agentes-grid{display:grid;grid-template-columns:250px minmax(0,1fr)!important;gap:0!important;align-items:stretch!important;border-top:1px solid #e8e8e8;border-bottom:1px solid #e8e8e8;background:#fff}
  .agents-list{display:grid!important;gap:0!important;position:static!important;align-content:start;border-right:1px solid #e8e8e8;background:#f7f7f7}
  .agent-tab{width:100%;min-height:60px;padding:10px 11px!important;border:0!important;border-bottom:1px solid #e9e9e9!important;border-radius:0!important;grid-template-columns:30px minmax(0,1fr) auto!important;gap:9px!important;color:#444!important;background:transparent!important;box-shadow:none!important}
  .agent-tab.active{border-color:#e9e9e9!important;color:#111!important;background:#fff!important;box-shadow:inset 2px 0 0 #111!important}
  .agent-icon{width:28px!important;height:28px!important;border-radius:6px!important;color:#333!important;background:#fff!important;border:1px solid #ddd}
  .agent-tab strong{font-size:11.5px!important;font-weight:700!important}
  .agent-tab span{margin-top:2px!important;color:#777!important;font-size:9.5px!important;line-height:1.3!important}
  .agent-tab em{padding:3px 6px!important;border-radius:6px!important;font-size:9px!important;font-weight:700!important}
  .agent-panel{min-width:0;padding:0!important;border:0!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}
  .panel-head{min-height:60px;padding:12px 14px!important;border-bottom:1px solid #ececec!important;align-items:center!important}
  .panel-head h2{margin:0 0 3px!important;color:#171717!important;font-size:15px!important;line-height:1.2!important;font-weight:700!important;letter-spacing:-.02em!important}
  .panel-head p{margin:0!important;color:#777!important;font-size:10.5px!important;line-height:1.35!important}
  .tabs-line{margin:0!important;padding:0 14px!important;gap:16px!important;border-bottom:1px solid #ececec;flex-wrap:nowrap!important}
  .tabs-line button{min-height:42px!important;padding:0!important;border:0!important;border-bottom:2px solid transparent!important;border-radius:0!important;color:#666!important;background:transparent!important;font-size:10.5px!important;font-weight:700!important}
  .tabs-line button.active{border-bottom-color:#111!important;color:#111!important;background:transparent!important}
  .config-grid{margin:0!important;padding:14px!important;gap:10px!important}
  .field{gap:4px!important}
  .field span{color:#686868!important;font-size:10px!important;font-weight:700!important;letter-spacing:.03em}
  input,select,textarea{border:1px solid #d8d8d8!important;border-radius:7px!important;color:#171717!important;background:#fff!important;font-size:11.5px!important;font-weight:600!important;box-shadow:none!important}
  input,select{min-height:34px!important}
  textarea{min-height:250px!important;padding:10px!important}
  input:read-only{color:#777!important;background:#f7f7f7!important}
  input:focus,select:focus,textarea:focus{border-color:#111!important;box-shadow:0 0 0 2px rgba(0,0,0,.05)!important}
  .box{margin:0!important;padding:14px!important;border:0!important;border-top:1px solid #ececec!important;border-radius:0!important;background:#fff!important;box-shadow:none!important}
  .box h3{color:#171717!important;font-size:12.5px!important;font-weight:700!important;letter-spacing:-.01em!important}
  .box p{margin:3px 0 9px!important;color:#777!important;font-size:10.5px!important}
  .chip{min-height:28px!important;padding:0 4px 0 8px!important;border-radius:7px!important;font-size:10.5px!important;font-weight:650!important}
  .empty{border:0!important;border-radius:0!important;color:#777!important;background:#fff!important;font-size:10.5px!important}
  .flow ol{margin-top:7px!important;color:#444!important;font-size:10.5px!important;line-height:1.55!important}
  .actions{margin:0!important;padding:12px 14px!important;border-top:1px solid #ececec;background:#fff}
  .message-editor{margin:0!important}
  .template-loading{min-height:260px!important;border:0!important;border-radius:0!important;color:#777!important}
  .editor-top{padding:12px 14px!important;border:0!important;border-bottom:1px solid #ececec!important;border-radius:0!important;background:#fff!important}
  .editor-top h3{font-size:13px!important;font-weight:700!important}
  .editor-top p{margin-top:3px!important;color:#777!important;font-size:10.5px!important}
  .editor-status span{padding:4px 7px!important;border-radius:6px!important;font-size:9.5px!important;font-weight:700!important}
  .check-row{min-height:34px!important;padding:0 9px!important;border-radius:7px!important;font-size:10.5px!important;font-weight:650!important}
  .template-grid{margin:0!important;padding:14px!important;grid-template-columns:minmax(0,1.15fr) minmax(300px,.85fr)!important;gap:14px!important}
  .vars-box,.preview-card,.hint-card{padding:12px!important;border:1px solid #e5e5e5!important;border-radius:8px!important;background:#fff!important;box-shadow:none!important}
  .vars-box strong,.preview-card strong,.hint-card strong{font-size:11.5px!important;font-weight:700!important}
  .vars-box p,.hint-card p{color:#777!important;font-size:10px!important}
  .vars-list button{min-height:26px!important;padding:0 7px!important;border:1px solid #d8d8d8!important;border-radius:6px!important;color:#333!important;background:#fff!important;font-size:9.5px!important;font-weight:650!important}
  .preview-head button{min-height:28px!important;padding:0 8px!important;border-radius:6px!important;font-size:9.5px!important;font-weight:650!important}
  .preview-card pre{padding:11px!important;border:1px solid #e5e5e5!important;border-radius:7px!important;color:#171717!important;background:#f7f7f7!important;font-size:11px!important;line-height:1.5!important}
  @media(max-width:1050px){.agentes-grid{grid-template-columns:1fr!important}.agents-list{grid-template-columns:repeat(2,minmax(0,1fr))!important;border-right:0;border-bottom:1px solid #e8e8e8}.template-grid{grid-template-columns:1fr!important}}
  @media(max-width:760px){.agentes-page{padding:14px 12px 24px!important}.agentes-header,.panel-head,.editor-top{display:grid!important}.metrics-strip{grid-template-columns:repeat(2,minmax(0,1fr))!important}.metric-item:nth-child(2){border-right:0}.metric-item:nth-child(-n+2){border-bottom:1px solid #ececec}.agents-list,.config-grid{grid-template-columns:1fr!important}.field.span-2,.span-2{grid-column:span 1!important}.admin-input{grid-template-columns:1fr!important}.soft-btn,.primary-btn{width:100%}.actions{display:grid!important}.tabs-line{overflow-x:auto}.tabs-line button{min-width:max-content}}

`;
